import * as React from 'react';
import * as Redux from 'redux';
import { htmlEncode } from '../../../functions/html-encode';

export interface ITextboxProps {
    id: string;
    onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
    onKeyPress?: (e: React.FormEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FormEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FormEvent<HTMLInputElement>) => void;
    hasError?: boolean;
    errorText?: string;
    labelText?: string;
    defaultValue?: string;
    placeHolder?: string;
    customClasses?: string;
    required?: boolean;
    value?: string;
    autoFocus?: boolean;
    icon?: JSX.Element;
    showOptionalRequired?: boolean;
    type?: string;
    disableLastPass?: boolean;
    requiredText?: string;
    optionalText?: string;
    disabled?: boolean;
    isControlled?: boolean;
    testID?: string;
    autoCompleteResults?: JSX.Element;
}

/// <summary>
/// This class can be used for a textbox with built in styling and handling for the AAP error state
/// </summary>
/// <param name="id">The html id of the textbox. Required to associate the label with the textbox</param>
/// <param name="onInput">The callback function for when the textbox input changes. Current textbox text should be saved in a state variable</param>
/// <param name="hasError">Determines whether or not to show the error state (optional, default=false)</param>
/// <param name="errorText">The error message displayed when the textbox is in its error state (optional, default='')</param>
/// <param name="labelText">The text shown for the label (optional, default='')</param>
/// <param name="defaultValue">Default text to display in the textbox (optional, default='')</param>
/// <param name="placeHolder">Default placeholder text (optional, default='')</param>
/// <param name="customClasses">Used to supply any additional classes to the surrounding textbox div (optional, default='')</param>
/// <returns></returns>
export class Textbox extends React.Component<ITextboxProps, any> {
    static defaultProps = {
        onInput: () => { },
        onKeyPress: () => { },
        onKeyDown: () => { },
        onBlur: () => { },
        onFocus: () => { },
        hasError: false,
        errorText: '',
        labelText: '',
        defaultValue: '',
        value: '',
        placeHolder: '',
        customClasses: '',
        required: false,
        autoFocus: false,
        icon: null,
        type: 'text',
        disableLastPass: false,
        showOptionalRequired: false,
        requiredText: '',
        optionalText: '',
        disabled: false,
        testID: '',
    }

    GetClassNames() {
        let FORM_CLASSES = " form-element";
        let REQUIRED_CLASS = " form-element-required";
        let INVALID_CLASS = " form-element-invalid";
        let classes = this.props.customClasses + FORM_CLASSES;

        if (this.props.hasError)
            classes += INVALID_CLASS;

        if (this.props.required)
            classes += REQUIRED_CLASS;

        return classes;
    }

    onInputHandler(e: React.FormEvent<HTMLInputElement>) {
        this.props.onInput(e);
    }

    onKeyPressHandler(e: any) {
        this.props.onKeyPress(e);
    }

    onKeyDownHandler(e: any) {
        this.props.onKeyDown(e);
    }

    onBlurHandler(e: any) {
        this.props.onBlur(e);
    }

    onFocusHandler(e: any) {
        this.props.onFocus(e);
    }

    renderOptionalRequired() {
        const { showOptionalRequired, required, requiredText, optionalText } = this.props;

        if (showOptionalRequired) {
            if (required) {
                return <span className="form-element-label-sub">{requiredText}</span>;
            }
            return <span className="form-element-label-sub">{optionalText}</span>;
        }
        return null;
    }

    render() {
        const { value, defaultValue, placeHolder, hasError, errorText, showOptionalRequired, labelText, customClasses, required, id, autoFocus, disableLastPass, type, isControlled } = this.props;        

        let boundInput = this.onInputHandler.bind(this);
        let boundKeyPress = this.onKeyPressHandler.bind(this);
        let boundonKeyDown = this.onKeyDownHandler.bind(this);
        let boundOnBlur = this.onBlurHandler.bind(this);
        let boundOnFocus = this.onFocusHandler.bind(this);

        let uncontrolledComponentProps = {
            defaultValue: value != null && value.length > 0 ? undefined : defaultValue,
        }

        return (
            <div
                className={this.GetClassNames()}
                id={`${id}-container`}
                data-testid={`${this.props.testID}-container`}
            >
                <label className="form-element-label" htmlFor={id}>
                    <span className="form-element-label-main">{labelText}</span>
                    {this.renderOptionalRequired()}
                </label>
                <div className="icon-container">
                    <input className="form-element-input"
                        type={type ? type : 'text'}
                        id={id}
                        data-testid={this.props.testID}
                        autoFocus={autoFocus}
                        {...isControlled ? null : uncontrolledComponentProps}
                        onChange={boundInput}
                        onKeyDown={boundonKeyDown}
                        onBlur={boundOnBlur}
                        onFocus={boundOnFocus}
                        value={isControlled ? (value || "") : (value == null || value.length <= 0 ? undefined : value)}
                        onKeyPress={boundKeyPress}
                        placeholder={placeHolder}
                        required={required}
                        data-lpignore={disableLastPass.toString()}
                        disabled={this.props.disabled}
                    />
                    {this.props.icon}
                    {this.props.autoCompleteResults}
                </div>
                <div className="form-element-error">
                    <svg className="form-element-error-icon">
                        <use xlinkHref="#icon-error"></use>
                    </svg>
                    <div className="form-element-error-text" dangerouslySetInnerHTML={htmlEncode(errorText)}>
                    </div>
                </div>
            </div>
        );
    }
}